import React, { useEffect, useMemo, useState } from 'react';
import { DndContext, DragEndEvent, useDraggable } from '@dnd-kit/core';
import TopNavBar from '../../components/TopNavBar/TopNavBar';
import styles from './ForestDiversity.module.css';
import { Species, calculateSid, speciesList } from './speciesData';
import forestImage from '../../assets/sid/foresttransectsmall.jpg';

type Leaf = {
  id: string;
  speciesId: string;
  x: number;
  y: number;
  angle: number;
};

type DraggableLeafProps = { leaf: Leaf; species: Species };

const DraggableLeaf: React.FC<DraggableLeafProps> = ({ leaf, species }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: leaf.id,
  });

  const baseStyle: React.CSSProperties = {
    left: `${leaf.x}px`,
    top: `${leaf.y}px`,
    transform: `rotate(${leaf.angle}deg)`,
  };

  const style = transform
    ? {
        ...baseStyle,
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0) rotate(${leaf.angle}deg)`,
      }
    : baseStyle;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={style}
      className={[styles.leaf, isDragging ? styles.leafDragging : ''].join(' ')}
    >
      <img src={species.leafImage} alt={species.name} />
    </div>
  );
};

const ForestDiversity: React.FC = () => {
  const randomPilePosition = () => {
    const centerX = 320;
    const centerY = 240;
    const r = Math.random() * 90;
    const theta = Math.random() * Math.PI * 2;
    return {
      x: centerX + r * Math.cos(theta),
      y: centerY + r * Math.sin(theta),
    };
  };

  const [positions, setPositions] = useState<Record<string, { x: number; y: number }>>({});
  const [answers, setAnswers] = useState<
    Record<
      string,
      { num: string; nn1: string; numStatus?: 'correct' | 'wrong'; nn1Status?: 'correct' | 'wrong' }
    >
  >({});
  const [totals, setTotals] = useState<{
    n: string;
    sum: string;
    d?: string;
    nStatus?: 'correct' | 'wrong';
    sumStatus?: 'correct' | 'wrong';
    dStatus?: 'correct' | 'wrong';
  }>({
    n: '',
    sum: '',
    d: '',
  });

  const leaves = useMemo<Leaf[]>(() => {
    const unordered = speciesList.flatMap((species) =>
      Array.from({ length: species.count }, (_, i) => ({
        id: `${species.id}-${i}`,
        speciesId: species.id,
        ...randomPilePosition(),
        angle: Math.random() * 40 - 20,
      })),
    );
    // Shuffle to randomize stacking/z-order so one species isn't always on top.
    for (let i = unordered.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [unordered[i], unordered[j]] = [unordered[j], unordered[i]];
    }
    return unordered;
  }, []);

  useEffect(() => {
    const pos: Record<string, { x: number; y: number }> = {};
    const ans: Record<
      string,
      { num: string; nn1: string; numStatus?: 'correct' | 'wrong'; nn1Status?: 'correct' | 'wrong' }
    > = {};
    leaves.forEach((leaf) => {
      const p = randomPilePosition();
      pos[leaf.id] = { x: p.x, y: p.y };
    });
    setPositions(pos);
    speciesList.forEach((s) => {
      ans[s.id] = { num: '', nn1: '' };
    });
    setAnswers(ans);
    setTotals({ n: '', sum: '', d: '' });
  }, [leaves]);

  const counts = useMemo(() => {
    const result: Record<string, number> = {};
    speciesList.forEach((s) => {
      result[s.id] = s.count;
    });
    return result;
  }, []);

  const { n, sumNn1, sid } = useMemo(() => calculateSid(counts), [counts]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, delta } = event;
    setPositions((prev) => {
      const prevPos = prev[String(active.id)] || { x: 0, y: 0 };
      const next = { ...prev };
      next[String(active.id)] = {
        x: prevPos.x + delta.x,
        y: prevPos.y + delta.y,
      };
      return next;
    });
  };

  const reset = () => {
    const pos: Record<string, { x: number; y: number }> = {};
    leaves.forEach((leaf) => {
      const p = randomPilePosition();
      pos[leaf.id] = { x: p.x, y: p.y };
    });
    setPositions(pos);
    const ans: Record<string, { num: string; nn1: string }> = {};
    speciesList.forEach((s) => {
      ans[s.id] = { num: '', nn1: '' };
    });
    setAnswers(ans);
    setTotals({ n: '', sum: '', d: '' });
  };

  const checkSpecies = (id: string, field: 'num' | 'nn1') => {
    const actual = field === 'num' ? counts[id] || 0 : (counts[id] || 0) * ((counts[id] || 0) - 1);
    const entered = field === 'num' ? answers[id]?.num : answers[id]?.nn1;
    const parsed = Number(entered);
    const status = !Number.isNaN(parsed) && parsed === actual ? 'correct' : 'wrong';
    setAnswers((prev) => ({
      ...prev,
      [id]: { ...prev[id], [`${field}Status`]: status },
    }));
  };

  const checkTotals = (field: 'n' | 'sum' | 'd') => {
    let actual: number;
    let entered: string;
    if (field === 'n') {
      actual = n;
      entered = totals.n;
    } else if (field === 'sum') {
      actual = sumNn1;
      entered = totals.sum;
    } else {
      actual = Number(sid.toFixed(3));
      entered = totals.d || '';
    }
    const parsed = Number(entered);
    const status =
      !Number.isNaN(parsed) &&
      (field === 'd' ? Math.abs(parsed - actual) < 0.001 : parsed === actual)
        ? 'correct'
        : 'wrong';
    setTotals((prev) => ({
      ...prev,
      [`${field}Status`]: status,
    }));
  };

  return (
    <div className={styles.page}>
      <TopNavBar />
      <DndContext onDragEnd={handleDragEnd}>
        <div className={styles.content}>
          <div className={styles.leftCol}>
            <div className={styles.hero}>
              <div>
                <p className={styles.kicker}>Ecology | Practice</p>
                <h1>Simpson&apos;s Index of Diversity</h1>
                <p className={styles.lead}>
                This worksheet will give you some practice determining Simpsons Index of Diversity for a forest community. 
                Sort the leaves from the pile below right into species groupings (use key below to help with identification). 
                One leaf was taken from each tree along the forest transect.
                </p>
              </div>
              <img src={forestImage} alt="Forest transect" className={styles.heroImage} />
            </div>
            <div className={styles.gameArea}>
              <div className={styles.leafPile}>
                {leaves.map((leaf) => {
                  const species = speciesList.find((s) => s.id === leaf.speciesId);
                  if (!species) return null;
                  const pos = positions[leaf.id] || { x: leaf.x, y: leaf.y };
                  return (
                    <DraggableLeaf key={leaf.id} leaf={{ ...leaf, ...pos }} species={species} />
                  );
                })}
              </div>
            </div>
          </div>
          <div className={styles.tableCard}>
          <div className={styles.formula}>
            <div className={styles.formulaTitle}>Formula</div>
              <div className={styles.formulaBody}>D = 1 - Σ n(n - 1) / T(T - 1)</div>
                <div className={styles.formulaMeta}>
                  N = total individuals; n = individuals in a species
                </div>
              </div>
            <table className={styles.table}>
              <colgroup>
                <col className={styles.colSpecies} />
                <col className={styles.colGapA} />
                <col className={styles.colNumber} />
                <col className={styles.colSubmit} />
                <col className={styles.colGapB} />
                <col className={styles.colNn1} />
                <col className={styles.colSubmit} />
              </colgroup>
              <thead>
                <tr>
                  <th className={styles.thLeft}>Species</th>
                  <th />
                  <th>number</th>
                  <th />
                  <th />
                  <th>n(n-1)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {speciesList.map((s) => {
                  const answer = answers[s.id] || { num: '', nn1: '' };
                  return (
                    <tr key={s.id}>
                      <td className={styles.speciesCell}>
                        <img src={s.keyImage} alt="" />
                        {s.name}
                      </td>
                      <td className={styles.blankCell} />
                      <td className={styles.inputCell}>
                        <input
                          type="number"
                          value={answer.num}
                          onChange={(e) =>
                            setAnswers((prev) => ({
                              ...prev,
                              [s.id]: { ...prev[s.id], num: e.target.value },
                            }))
                          }
                        />
                      </td>
                      <td className={styles.submitCell}>
                        <button type="button" onClick={() => checkSpecies(s.id, 'num')}>
                          submit
                        </button>
                        <span
                          className={
                            answer.numStatus === 'correct'
                              ? styles.statusGood
                              : answer.numStatus === 'wrong'
                              ? styles.statusBad
                              : ''
                          }
                        >
                          {answer.numStatus === 'correct' ? '✓' : answer.numStatus === 'wrong' ? '✗' : ''}
                        </span>
                      </td>
                      <td className={styles.blankCell} />
                      <td className={styles.inputCell}>
                        <input
                          type="number"
                          value={answer.nn1}
                          onChange={(e) =>
                            setAnswers((prev) => ({
                              ...prev,
                              [s.id]: { ...prev[s.id], nn1: e.target.value },
                            }))
                          }
                        />
                      </td>
                      <td className={styles.submitCell}>
                        <button type="button" onClick={() => checkSpecies(s.id, 'nn1')}>
                          submit
                        </button>
                        <span
                          className={
                            answer.nn1Status === 'correct'
                              ? styles.statusGood
                              : answer.nn1Status === 'wrong'
                              ? styles.statusBad
                              : ''
                          }
                        >
                          {answer.nn1Status === 'correct' ? '✓' : answer.nn1Status === 'wrong' ? '✗' : ''}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className={styles.blankFooterRow}>
                  <td colSpan={7} />
                </tr>
                <tr>
                  <td />
                  <td className={styles.blankCell}>
                    <span className={styles.footerLabel}>T =</span>
                  </td>
                  <td className={styles.footerCell}>
                    <input
                      type="number"
                      value={totals.n}
                      onChange={(e) => setTotals((prev) => ({ ...prev, n: e.target.value }))}
                    />
                  </td>
                  <td className={styles.submitCell}>
                    <button type="button" onClick={() => checkTotals('n')}>
                      submit
                    </button>
                    <span
                      className={
                        totals.nStatus === 'correct'
                          ? styles.statusGood
                          : totals.nStatus === 'wrong'
                          ? styles.statusBad
                          : ''
                      }
                    >
                      {totals.nStatus === 'correct' ? '✓' : totals.nStatus === 'wrong' ? '✗' : ''}
                    </span>
                  </td>
                  <td className={styles.blankCell}>
                    <span className={styles.footerLabel}>Σn(n-1) =</span>
                  </td>
                  <td className={styles.footerCell}>
                    <input
                      type="number"
                      value={totals.sum}
                      onChange={(e) => setTotals((prev) => ({ ...prev, sum: e.target.value }))}
                    />
                  </td>
                  <td className={styles.submitCell}>
                    <button type="button" onClick={() => checkTotals('sum')}>
                      submit
                    </button>
                    <span
                      className={
                        totals.sumStatus === 'correct'
                          ? styles.statusGood
                          : totals.sumStatus === 'wrong'
                          ? styles.statusBad
                          : ''
                      }
                    >
                      {totals.sumStatus === 'correct' ? '✓' : totals.sumStatus === 'wrong' ? '✗' : ''}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td />
                  <td className={styles.blankCell} style={{ paddingTop: '20px' }}>
                    <span className={styles.footerLabel}>D =</span>
                  </td>
                  <td className={styles.footerCell} style={{ paddingTop: '20px' }}>
                    <input
                      type="number"
                      step="0.001"
                      value={totals.d}
                      onChange={(e) => setTotals((prev) => ({ ...prev, d: e.target.value }))}
                    />
                  </td>
                  <td className={styles.submitCell} style={{ paddingTop: '20px' }}>
                    <button type="button" onClick={() => checkTotals('d')}>
                      submit
                    </button>
                    <span
                      className={
                        totals.dStatus === 'correct'
                          ? styles.statusGood
                          : totals.dStatus === 'wrong'
                          ? styles.statusBad
                          : ''
                      }
                    >
                      {totals.dStatus === 'correct' ? '✓' : totals.dStatus === 'wrong' ? '✗' : ''}
                    </span>
                  </td>
                  <td className={styles.blankCell} />
                  <td />
                  <td />
                </tr>
              </tfoot>
            </table>
            <button className={styles.resetButton} onClick={reset} type="button">
              Reset
            </button>
          </div>
        </div>
      </DndContext>
    </div>
  );
};

export default ForestDiversity;
